'use client';

import React, { useState } from 'react';
import { Star } from 'lucide-react';

/**
 * Звёзды супервизора.
 *
 * Отдельное измерение от балла чек-листа, и в интерфейсе они нигде не
 * складываются и не усредняются с ним: балл — арифметика правил, объяснимая
 * построчно, звёзды — суждение человека, послушавшего разговор.
 *
 * Снятие оценки шлёт `null`, а не `0`: ноль читался бы как «худшая оценка»,
 * а снятая оценка — это отсутствие суждения.
 */
export default function StarRating({
  value,
  size = 14,
  readOnly = false,
  busy = false,
  onChange,
}: {
  value: number | null;
  size?: number;
  readOnly?: boolean;
  busy?: boolean;
  onChange?: (rating: number | null) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value ?? 0;

  if (readOnly && value === null) {
    return <span className="text-xs text-slate-300">—</span>;
  }

  return (
    <span className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(null)}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly || busy}
          onMouseEnter={() => !readOnly && setHover(star)}
          onClick={() => onChange?.(star)}
          title={readOnly ? undefined : `Поставить ${star}`}
          className={readOnly ? 'cursor-default' : 'cursor-pointer disabled:opacity-50'}
        >
          <Star
            size={size}
            className={star <= shown ? 'text-amber-400' : 'text-slate-300'}
            fill={star <= shown ? 'currentColor' : 'none'}
          />
        </button>
      ))}

      {!readOnly && value !== null && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onChange?.(null)}
          className="ml-1.5 text-[10px] text-slate-400 hover:text-slate-600 disabled:opacity-50"
          title="Снять оценку — это не ноль, а отсутствие суждения"
        >
          снять
        </button>
      )}
    </span>
  );
}
